import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, IconButton, Popover } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useFavourites } from './useFavourites';
import { mergeVisibleOrder } from './pageMetadata';

const pageIcon = (path) => {
  if (path === '/' || path === '/home') return HomeOutlinedIcon;
  if (path.startsWith('/wallet')) return AccountBalanceWalletOutlinedIcon;
  if (path.startsWith('/voucher')) return ConfirmationNumberOutlinedIcon;
  if (path.startsWith('/parkit')) return LocalParkingOutlinedIcon;
  if (path.startsWith('/deliverit')) return LocalShippingOutlinedIcon;
  if (path.startsWith('/action-trail')) return HistoryOutlinedIcon;
  if (path.startsWith('/links')) return LinkOutlinedIcon;
  if (path.startsWith('/staff') || path.startsWith('/users') || path.startsWith('/user-tags')) return PeopleOutlineIcon;
  return DescriptionOutlinedIcon;
};

export default function FavouritesMenu() {
  const { owner, items, visible, busy, ready, status, metadata, save: persist } = useFavourites();
  const { pathname } = useLocation();
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState(null);
  const drag = useRef(null);
  useEffect(() => {
    setAnchor(null);
    setDraft(null);
    drag.current = null;
  }, [owner]);

  if (!owner) return null;
  const current = metadata(pathname);
  const pinned = current && items.find((item) => item.path === current.path);
  const close = () => { drag.current = null; setAnchor(null); setDraft(null); };
  const save = async (operation, body = {}) => {
    await persist(operation, body);
    if (currentOwner.current !== owner) return;
    setDraft(null);
  };
  const startDrag = (event, item) => {
    if (event.button !== 0 || busy) return;
    event.preventDefault();
    const list = event.currentTarget.closest('ul');
    // Capture on the stable list, not a handle whose DOM node moves during sorting.
    list.setPointerCapture(event.pointerId);
    const slots = [...list.querySelectorAll('[data-favourite-id]')].map((row) => {
      const rect = row.getBoundingClientRect();
      return (rect.top + rect.bottom) / 2;
    });
    drag.current = { id: item.id, original: visible, next: visible, slots };
  };
  const moveDrag = (event) => {
    if (!drag.current) return;
    // Fixed slot boundaries avoid swapping back as rows move underneath the pointer.
    const { slots } = drag.current;
    let target = 0;
    while (target < slots.length - 1 && event.clientY > (slots[target] + slots[target + 1]) / 2) target += 1;
    const source = drag.current.next.findIndex((item) => item.id === drag.current.id);
    if (target < 0 || source === target) return;
    const next = [...drag.current.next];
    next.splice(target, 0, next.splice(source, 1)[0]);
    drag.current.next = next;
    setDraft(next);
  };
  const finishDrag = () => {
    const pending = drag.current;
    drag.current = null;
    if (pending && pending.next.some((item, index) => item.id !== pending.original[index].id)) {
      save('order', { ids: mergeVisibleOrder(items, pending.next) });
    } else setDraft(null);
  };
  const keyboardMove = (event, index) => {
    if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const target = index + (event.key === 'ArrowUp' ? -1 : 1);
    if (target < 0 || target >= visible.length) return;
    const next = [...visible];
    next.splice(target, 0, next.splice(index, 1)[0]);
    save('order', { ids: mergeVisibleOrder(items, next) });
  };

  return (
    <>
      <IconButton onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ color: 'var(--content)', '&:hover': { backgroundColor: 'var(--surface-hover)' } }}
        aria-label="Favourite pages" aria-haspopup="dialog" aria-expanded={Boolean(anchor)} aria-controls={anchor ? 'favourites-menu' : undefined}>
        <StarBorderIcon />
      </IconButton>
      <Popover id="favourites-menu" anchorEl={anchor} open={Boolean(anchor)} onClose={busy ? undefined : close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: {
          width: 360, maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh',
          backgroundColor: 'var(--surface)', backgroundImage: 'none', color: 'var(--content)',
          border: '1px solid var(--divider)', boxShadow: 'var(--panel-shadow)',
          '& a:hover, & a:focus-visible, & .MuiIconButton-root:hover, & .MuiButton-root:hover': {
            backgroundColor: 'var(--surface-hover)',
          },
          '& .MuiIconButton-root': { color: 'var(--content-secondary)' },
          '& .MuiButton-root': { color: 'var(--pk-brand-ink)' },
          '& .Mui-disabled': { color: 'var(--content-muted)' },
        } }}>
        <div role="dialog" aria-label="Favourite pages" className="py-2">
        {!pinned && <Button fullWidth disabled={!ready || busy || !current} onClick={() => save('pin', current)}>
          Pin current page
        </Button>}
        {busy && <p className="px-4 py-2 text-sm text-theme-muted">Updating favourites…</p>}
        {status === 'failed' && <Button fullWidth onClick={() => save('load')}>Unable to load or save. Retry</Button>}
        {!busy && !visible.length && <p className="px-4 py-2 text-sm text-theme-muted">No favourites yet. Pin a page to get started.</p>}
        <ul aria-label="Saved favourites" onPointerMove={moveDrag} onPointerUp={finishDrag}
          onPointerCancel={() => { drag.current = null; setDraft(null); }}
          onLostPointerCapture={() => { drag.current = null; setDraft(null); }}>
        {(draft || visible).map((item, index) => {
          const PageIcon = pageIcon(item.path);
          return (
          <li key={item.id} data-favourite-id={item.id} className="flex items-center px-2"
            style={{ backgroundColor: draft && drag.current?.id === item.id ? 'var(--surface-hover)' : undefined }}>
            <Link to={item.path} onClick={(event) => { if (busy || drag.current) event.preventDefault(); else close(); }}
              className="flex flex-1 min-w-0 items-center gap-3 rounded px-2 py-3 text-sm" style={{ color: 'inherit' }}>
              <PageIcon fontSize="small" sx={{ flexShrink: 0, color: 'var(--content-secondary)' }} />
              <span className="min-w-0 break-words">{item.label}</span>
            </Link>
            <IconButton size="small" aria-label={`Reorder ${item.label}`} title="Drag to reorder, or use Up/Down arrow keys"
               disabled={!ready || visible.length < 2}
              sx={{ cursor: draft ? 'grabbing' : 'grab', touchAction: 'none' }}
              onPointerDown={(event) => startDrag(event, item)}
              onKeyDown={(event) => keyboardMove(event, index)}><DragIndicatorIcon fontSize="small" /></IconButton>
             <IconButton size="small" aria-label={`Remove ${item.label}`} disabled={!ready || Boolean(draft)} onClick={() => save('remove', { id: item.id })}><CloseIcon fontSize="small" /></IconButton>
          </li>
          );
        })}
        </ul>
        </div>
      </Popover>
    </>
  );
}
